'use client';

import { usePathname } from 'next/navigation';
import { useSiteSettings } from '@/components/providers';
import { businessAreaNavItem, getAnchorIds, isNavItemActive, isNavItemAncestor, resolveHref } from './navigation.utils';
import { useScrollSpy } from '@/hooks';
import { BusinessAreasMegaMenu } from './BusinessAreasMegaMenu';
import { NavLink } from './NavLink';
import type { NavItemDto } from '@/lib/sanity/dto';

/**
 * The horizontal navigation. Its items are a mix of plain links and the
 * Geschäftsbereiche menu; the editor decides the order, so the renderer
 * switches on `_type` rather than assuming a fixed position.
 */
export function MainNavigation() {
    const settings = useSiteSettings();
    const items = settings.navigation?.main ?? [];
    const areas = settings.businessAreas ?? [];
    const pathname = usePathname();

    const linkItems = items.filter((item): item is NavItemDto & { _type: 'navItem' } => item._type === 'navItem');
    const activeAnchorId = useScrollSpy({ ids: getAnchorIds(linkItems) });

    // The menu counts as current while any of its areas is the open page.
    const isAreaPage = areas.some((area) => resolveHref(businessAreaNavItem(area)).split('#')[0] === pathname);

    return (
        <nav className="site-nav" aria-label="Hauptnavigation">
            {items.map((item) => {
                if (item._type === 'businessAreasNavItem') {
                    return <BusinessAreasMegaMenu key={item._key} item={item} areas={areas} isCurrent={isAreaPage} />;
                }

                return (
                    <NavLink
                        key={item._key}
                        item={item}
                        isActive={isNavItemActive(item, pathname, activeAnchorId)}
                        isAncestor={isNavItemAncestor(item, pathname)}
                        className="site-nav__link"
                    />
                );
            })}
        </nav>
    );
}
